import { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { BroadcastPopup } from './BroadcastPopup';
import RichiediSupportoButton from './RichiediSupportoButton';
import NotificationBell from './NotificationBell';
import { AyvensQuestionnaireDialog } from './ayvens/AyvensQuestionnaireDialog';
import { CatalogUpdateBanner } from './CatalogUpdateBanner';
import { 
  Car, 
  LayoutDashboard, 
  FileText, 
  FileCheck,
  Users, 
  Upload, 
  LogOut, 
  Menu, 
  X,
  ChevronRight,
  ChevronLeft,
  BookUser,
  Download,
  Store,
  Briefcase,
  Bell,
  UserPlus,
  FolderOpen,
  Eye,
  Crown,
  User,
  Megaphone,
  Building2,
  LifeBuoy,
  Mail,
  Phone,
  Settings,
  Calculator,
  Handshake,
  Tag
} from 'lucide-react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Componente NavItem (fuori dal render per evitare re-render)
const NavItem = ({ to, icon: Icon, label, end, badge, badgeColor = 'violet', badgeTestId, collapsed, onItemClick }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) => cn('sidebar-link', isActive && 'active', collapsed && 'justify-center px-2')}
    onClick={onItemClick}
    data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
    title={collapsed ? label : undefined}
  >
    <Icon className="w-5 h-5 flex-shrink-0" />
    {!collapsed && <span className="truncate">{label}</span>}
    {badge > 0 && (
      <span
        className={cn(
          "flex items-center justify-center min-w-5 h-5 text-xs font-bold rounded-full text-white",
          badgeColor === 'red' ? "bg-red-600 animate-pulse" : "bg-violet-600",
          collapsed ? "absolute -top-1 -right-1" : "ml-auto"
        )}
        data-testid={badgeTestId}
      >
        {badge > 99 ? '99+' : badge}
      </span>
    )}
    {!collapsed && !badge && <ChevronRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />}
  </NavLink>
);

// Componente Internal Link NavItem per navigazione stessa pagina (fuori dal render)
const InternalLinkItem = ({ to, icon: Icon, label, collapsed, onItemClick }) => (
  <NavLink
    to={to}
    className={({ isActive }) => cn(
      'sidebar-link group',
      isActive && 'active',
      collapsed && 'justify-center px-2'
    )}
    onClick={onItemClick}
    data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
    title={collapsed ? label : undefined}
  >
    <Icon className="w-5 h-5 flex-shrink-0" />
    {!collapsed && <span className="truncate">{label}</span>}
    {!collapsed && <ChevronRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />}
  </NavLink>
);

const DashboardLayout = () => {
  const { user, logout, getAuthHeader, impersonatedUser, startImpersonate, stopImpersonate, activeConvenzione, exitConvenzione } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [pendingDocsCount, setPendingDocsCount] = useState(0);
  const [clienteContractsCount, setClienteContractsCount] = useState(0);
  const [newLeadsCount, setNewLeadsCount] = useState(0);  // Badge per nuovi lead agente
  const [broadcastCount, setBroadcastCount] = useState(0);  // Badge per broadcast non letti
  const [adminActivitiesCount, setAdminActivitiesCount] = useState(0);  // Badge per attività admin pendenti
  const [capogruppoList, setCapogruppoList] = useState([]);  // Lista Capogruppo per dropdown Impersonate
  const [myReparto, setMyReparto] = useState(null);  // Info reparto corrente per white label
  const [convenzioneFull, setConvenzioneFull] = useState(null);  // Info completa convenzione attiva (per co-branding logo)
  
  // Carica info completa della convenzione attiva (per mostrare logo in top-left)
  // 2.6.18 — Per gli utenti CLIENTE, la convenzione viene caricata da /api/client/my-convenzione
  // (il cliente non ha una URL /convenzioni/{id} attiva, ma solo il campo user.convenzione_id).
  useEffect(() => {
    // Caso 1: agente/admin dentro workspace convenzione → chiama /admin/convenzioni/{id}
    if (activeConvenzione?.id) {
      (async () => {
        try {
          const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/convenzioni/${activeConvenzione.id}`, { headers: getAuthHeader() });
          setConvenzioneFull(res.data);
        } catch { setConvenzioneFull(null); }
      })();
      return;
    }
    // Caso 2: cliente autenticato con convenzione → chiama /client/my-convenzione
    if (user?.role === 'cliente' || user?.role === 'cliente_smart') {
      (async () => {
        try {
          const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/client/my-convenzione`, { headers: getAuthHeader() });
          if (res.data?.has_convenzione) setConvenzioneFull(res.data);
          else setConvenzioneFull(null);
        } catch { setConvenzioneFull(null); }
      })();
      return;
    }
    setConvenzioneFull(null);
  }, [activeConvenzione?.id, user?.role, getAuthHeader]);
  const [supportoInfo, setSupportoInfo] = useState(null);  // Dati supporto per widget header
  const [showSupportoPopup, setShowSupportoPopup] = useState(false);  // Toggle popup supporto
  
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'top_agent';
  const isSuperAdmin = user?.role === 'super_admin';
  const isTopAgent = user?.role === 'top_agent';
  
  // Durante l'impersonificazione, assumiamo i permessi dell'utente impersonato
  const impersonatingTopAgent = impersonatedUser?.role === 'top_agent';
  const impersonatingAgent = impersonatedUser?.role === 'agente';
  
  // Se impersoniamo qualcuno, usiamo i suoi permessi
  // Se impersoniamo un Top Agent: vede menu admin MA NON Backup/Export né Importa Excel
  // Se impersoniamo un Agente: NON vede menu admin
  const effectiveIsSuperAdmin = impersonatedUser ? false : isSuperAdmin;
  const effectiveIsTopAgent = impersonatedUser ? impersonatingTopAgent : isTopAgent;
  const showAdminMenus = impersonatedUser 
    ? impersonatingTopAgent  // Solo se impersono Top Agent
    : isAdmin;               // Altrimenti logica normale
  
  // Backup/Export e Importa Excel: MAI durante impersonificazione
  const showBackupExport = isSuperAdmin && !impersonatedUser;
  const canAccessImportExcel = (isSuperAdmin || (user?.role === 'admin' && myReparto?.is_sede_centrale)) && !impersonatedUser;
  
  // Vetrina visibile solo per Sede Centrale o Super Admin
  const showVetrina = myReparto?.vetrina_enabled || isSuperAdmin || myReparto?.is_sede_centrale;
  
  // Fetch info reparto corrente (o del reparto dell'utente impersonato)
  useEffect(() => {
    const fetchMyReparto = async () => {
      if (!user) return;
      try {
        // Se stiamo impersonando, usa il reparto dell'utente impersonato
        const targetRepartoId = impersonatedUser?.reparto_id;
        
        if (targetRepartoId) {
          // Carica info del reparto impersonato
          const response = await axios.get(`${API}/reparti/${targetRepartoId}`, { headers: getAuthHeader() });
          setMyReparto(response.data);
        } else {
          // Carica info del proprio reparto
          const response = await axios.get(`${API}/my-reparto`, { headers: getAuthHeader() });
          setMyReparto(response.data);
        }
      } catch (error) {
        console.error('Error fetching my reparto:', error);
      }
    };
    fetchMyReparto();
  }, [user, getAuthHeader, impersonatedUser]);
  
  // Fetch lista agenti per dropdown Impersonate (solo Admin)
  const [agentsList, setAgentsList] = useState({ capogruppo: [], altri_agenti: [] });
  
  useEffect(() => {
    const fetchAgentsList = async () => {
      if (!user || !['admin', 'super_admin', 'top_agent'].includes(user.role)) return;
      try {
        const response = await axios.get(`${API}/capogruppo-list`, { headers: getAuthHeader() });
        // Ordinamento alfabetico per name, mantenendo i due gruppi distinti (Capogruppo / Altri Agenti)
        const sortByName = (a, b) => (a?.name || '').localeCompare(b?.name || '', 'it', { sensitivity: 'base' });
        const capogruppoSorted = [...(response.data?.capogruppo || [])].sort(sortByName);
        const altriSorted = [...(response.data?.altri_agenti || [])].sort(sortByName);
        setAgentsList({ capogruppo: capogruppoSorted, altri_agenti: altriSorted });
        // Mantieni anche capogruppoList per compatibilità
        setCapogruppoList([...capogruppoSorted, ...altriSorted]);
      } catch (error) {
        console.error('Error fetching agents list:', error);
      }
    };
    fetchAgentsList();
  }, [user, getAuthHeader]);
  
  // Handler per cambio impersonation
  const handleImpersonateChange = (value) => {
    if (value === 'admin') {
      stopImpersonate();
    } else {
      // Cerca in entrambe le liste
      const allAgents = [...agentsList.capogruppo, ...agentsList.altri_agenti];
      const agent = allAgents.find(a => a.id === value);
      if (agent) {
        startImpersonate(agent);
      }
    }
    // Naviga alla dashboard per ricaricare i dati
    navigate('/dashboard');
    // Forza refresh della pagina corrente
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };
  
  // Auto-collapse sidebar on smaller screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1280 && window.innerWidth >= 1024) {
        setSidebarCollapsed(true);
      } else if (window.innerWidth >= 1280) {
        setSidebarCollapsed(false);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch conteggio offerte in attesa documentazione (per badge agenti/admin)
  useEffect(() => {
    const fetchPendingDocsCount = async () => {
      if (!user || ['cliente_smart', 'cliente'].includes(user.role)) return;
      try {
        const response = await axios.get(`${API}/offers/pending-docs/count`, { 
          headers: getAuthHeader() 
        });
        setPendingDocsCount(response.data.count || 0);
      } catch (error) {
        console.error('Error fetching pending docs count:', error);
      }
    };
    
    fetchPendingDocsCount();
    // Aggiorna ogni 30 secondi
    const interval = setInterval(fetchPendingDocsCount, 30000);
    return () => clearInterval(interval);
  }, [user, getAuthHeader]);

  // Fetch conteggio attività admin pendenti (richieste agente, conflitti, lead da assegnare)
  useEffect(() => {
    const fetchAdminActivities = async () => {
      // Solo per admin, super_admin, top_agent
      if (!user || !['admin', 'super_admin', 'top_agent'].includes(user.role)) return;
      try {
        const response = await axios.get(`${API}/admin/pending-activities/count`, { 
          headers: getAuthHeader() 
        });
        setAdminActivitiesCount(response.data.total || 0);
      } catch (error) {
        console.error('Error fetching admin activities count:', error);
      }
    };
    
    fetchAdminActivities();
    // Aggiorna ogni 30 secondi
    const interval = setInterval(fetchAdminActivities, 30000);
    return () => clearInterval(interval);
  }, [user, getAuthHeader]);

  // Fetch conteggio nuovi lead per agenti E admin (badge rosso "Dashboard")
  useEffect(() => {
    const fetchNewLeadsCount = async () => {
      // Sia agenti che admin possono avere lead assegnati a sé
      if (!user || ['cliente_smart', 'cliente'].includes(user.role)) return;
      try {
        const response = await axios.get(`${API}/agent/new-leads/count`, { 
          headers: getAuthHeader() 
        });
        setNewLeadsCount(response.data.count || 0);
      } catch (error) {
        console.error('Error fetching new leads count:', error);
      }
    };
    
    fetchNewLeadsCount();
    // Aggiorna ogni 30 secondi
    const interval = setInterval(fetchNewLeadsCount, 30000);
    return () => clearInterval(interval);
  }, [user, getAuthHeader]);

  // Fetch conteggio contratti cliente (per mostrare/nascondere voce menu)
  useEffect(() => {
    const fetchClienteContracts = async () => {
      if (!user || !['cliente_smart', 'cliente'].includes(user.role)) return;
      try {
        const response = await axios.get(`${API}/cliente/contratti/count`, { 
          headers: getAuthHeader() 
        });
        setClienteContractsCount(response.data.count || 0);
      } catch (error) {
        console.error('Error fetching cliente contracts count:', error);
      }
    };
    
    fetchClienteContracts();
  }, [user, getAuthHeader]);

  // Fetch dati supporto per widget header
  useEffect(() => {
    const fetchSupportoInfo = async () => {
      if (!user) return;
      try {
        const response = await axios.get(`${API}/supporto/info`, {
          headers: getAuthHeader()
        });
        setSupportoInfo(response.data);
      } catch (error) {
        console.error('Error fetching supporto info:', error);
      }
    };
    
    fetchSupportoInfo();
  }, [user, getAuthHeader]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Verifica se l'utente è un cliente (accesso limitato)
  const isCliente = ['cliente_smart', 'cliente'].includes(user?.role);
  const isAgente = user?.role === 'agente';

  // Menu completo per Admin e Agente
  // Badge Dashboard: priorità al rosso (broadcast > attività admin > nuovi lead)
  // Per Admin: mostra attività pendenti (richieste agente, conflitti, lead da assegnare)
  // Per Agenti: mostra solo nuovi lead assegnati
  // Fase 1.4: badge iscritti convenzioni in attesa (Admin/SuperAdmin/TopAgent)
  const [convenzioniPendingCount, setConvenzioniPendingCount] = useState(0);
  // 2.6.4 — Conteggio convenzioni di cui l'utente è referente (per mostrare voce menu)
  const [convenzioniAssegnateCount, setConvenzioniAssegnateCount] = useState(0);
  useEffect(() => {
    // 2.6.7 — Anche gli agenti semplici (role='agente') possono essere referenti
    // di convenzioni: dobbiamo fetchare il conteggio anche per loro.
    const canSeeConvenzioniBadge = user && ['admin', 'super_admin', 'top_agent', 'agente'].includes(user.role) && !impersonatedUser;
    if (!canSeeConvenzioniBadge) {
      setConvenzioniPendingCount(0);
      setConvenzioniAssegnateCount(0);
      return;
    }
    let cancelled = false;
    const fetchPending = async () => {
      // Solo Admin/Top Agent/Super Admin hanno accesso al conteggio pending (route admin)
      if (!['admin', 'super_admin', 'top_agent'].includes(user.role)) return;
      try {
        const res = await axios.get(`${API}/admin/convenzioni-pending/counts`, { headers: getAuthHeader() });
        if (!cancelled) setConvenzioniPendingCount(res.data?.total || 0);
      } catch {
        if (!cancelled) setConvenzioniPendingCount(0);
      }
    };
    const fetchAssegnate = async () => {
      try {
        // 2.6.5 — badge sidebar: solo convenzioni di cui l'utente è EFFETTIVAMENTE referente
        const res = await axios.get(`${API}/agent/convenzioni-assegnate?only_mine=true`, { headers: getAuthHeader() });
        const list = res.data || [];
        // Escludi la convenzione di default (Machenoleggio) dal conteggio menu
        const activeCount = list.filter(c => !c.is_default).length;
        if (!cancelled) setConvenzioniAssegnateCount(activeCount);
      } catch {
        if (!cancelled) setConvenzioniAssegnateCount(0);
      }
    };
    fetchPending();
    fetchAssegnate();
    const interval = setInterval(fetchPending, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user, impersonatedUser, getAuthHeader]);

  const isAdminUser = ['admin', 'super_admin', 'top_agent'].includes(user?.role);
  const dashboardBadge = broadcastCount > 0 
    ? broadcastCount 
    : (isAdminUser && adminActivitiesCount > 0) 
      ? adminActivitiesCount 
      : newLeadsCount;
  const dashboardBadgeColor = broadcastCount > 0 
    ? 'red' 
    : (isAdminUser && adminActivitiesCount > 0) 
      ? 'red' 
      : (newLeadsCount > 0 ? 'red' : undefined);
  
  // Se convenzione attiva → link Dashboard punta all'overview della convenzione
  const dashboardTo = activeConvenzione?.id
    ? `/dashboard/convenzioni/${activeConvenzione.id}/overview`
    : '/dashboard';
  const dashboardLabel = activeConvenzione ? `Dashboard ${activeConvenzione.nome}` : 'Dashboard';

  // 2.6.16 — L'agente vede "Offerte in Lavorazione" SOLO dentro il workspace
  // di una convenzione (activeConvenzione). Nel menu standard fuori convenzione
  // la voce è nascosta perché si popola unicamente nel contesto convenzioni.
  const showOfferteInLavorazione = user?.role !== 'agente' || !!activeConvenzione;

  const fullNavItems = [
    // Dashboard con badge rosso se ci sono broadcast non letti, attività admin o nuovi lead assegnati
    { to: dashboardTo, icon: LayoutDashboard, label: dashboardLabel, end: !activeConvenzione, badge: dashboardBadge, badgeColor: dashboardBadgeColor },
    { to: '/dashboard/catalogo', icon: Car, label: 'Catalogo Veicoli' },
    { to: '/dashboard/offerte-mese', icon: Tag, label: 'Offerte del Mese' },
    { to: '/dashboard/rubrica', icon: BookUser, label: 'Rubrica Clienti' },
    { to: '/dashboard/portafoglio', icon: Briefcase, label: 'Portafoglio Clienti' },
    { to: '/dashboard/offerte', icon: FileText, label: 'Le mie Offerte' },
    { to: '/dashboard/offerte-attive', icon: Bell, label: 'Offerte Attive', badge: pendingDocsCount },
    ...(showOfferteInLavorazione
      ? [{ to: '/dashboard/offerte-in-lavorazione', icon: Handshake, label: 'Offerte in Lavorazione' }]
      : []),
    { to: '/dashboard/ordini', icon: FileCheck, label: 'Ordini' },
  ];

  // Fase 1.4 Punto 4: menu Cliente - "I Miei Preventivi" + "I Miei Contratti" (se ne ha)
  //                                   + "Contatti Utili" + "Il mio profilo"
  // - Outbound (Sede, senza convenzione_id): NIENTE configuratore. Solo preventivi ufficiali dell'agente.
  // - Inbound (Convenzione): stesse voci + configuratore (verrà aggiunto in Fase 2).
  const clienteNavItems = [
    // Sub-release 2.6 — Catalogo Offerte come primo item cliente
    { to: '/dashboard/catalogo-offerte', icon: Store, label: 'Catalogo Offerte', end: true },
    { to: '/dashboard/offerte', icon: FileText, label: 'I Miei Preventivi' },
    // Mostra "I miei Contratti" solo se il cliente ha almeno un contratto
    ...(clienteContractsCount > 0 ? [
      { to: '/dashboard/contratti', icon: FileCheck, label: 'I miei Contratti', badge: clienteContractsCount }
    ] : []),
    { to: '/dashboard/contatti-utili', icon: LifeBuoy, label: 'Contatti Utili' },
    { to: '/dashboard/profilo', icon: User, label: 'Il Mio Profilo' },
  ];

  // Seleziona il menu in base al ruolo
  const navItems = isCliente ? clienteNavItems : fullNavItems;

  // Admin Items - Backup/Export visibile solo se showBackupExport è true
  const adminItems = [
    // NASCOSTO - Gestione Pratiche non attiva per ora
    // { to: '/dashboard/pratiche', icon: FileCheck, label: 'Gestione Pratiche' },
    { to: '/dashboard/utenti', icon: Users, label: 'Gestione Utenti' },
    // 2.6.5 — Admin di reparto: accesso a Gestione Convenzioni (route già permessa)
    ...(user?.role === 'admin' && !impersonatedUser ? [
      { to: '/dashboard/convenzioni', icon: Handshake, label: 'Gestione Convenzioni', badge: convenzioniPendingCount, badgeColor: 'red', badgeTestId: 'pending-count-badge' },
      // Convenzioni Assegnate visibile solo se l'admin è referente di almeno 1 convenzione
      ...(convenzioniAssegnateCount > 0 ? [
        { to: '/dashboard/convenzioni-assegnate', icon: Building2, label: 'Convenzioni Assegnate', badge: convenzioniAssegnateCount, badgeColor: 'emerald', badgeTestId: 'convenzioni-assegnate-count' }
      ] : []),
    ] : []),
    // Backup/Export: solo Super Admin e MAI durante impersonificazione
    ...(showBackupExport ? [{ to: '/dashboard/export', icon: Download, label: 'Backup/Export' }] : []),
  ];
  
  // Menu esclusivo Super Admin - MAI durante impersonificazione
  const superAdminItems = effectiveIsSuperAdmin ? [
    { to: '/dashboard/reparti', icon: Building2, label: 'Gestione Reparti' },
    { to: '/dashboard/convenzioni', icon: Handshake, label: 'Gestione Convenzioni', badge: convenzioniPendingCount, badgeColor: 'red', badgeTestId: 'pending-count-badge' },
    // 2.6.4 — Convenzioni Assegnate (per gestione con vista workspace)
    ...(convenzioniAssegnateCount > 0 ? [
      { to: '/dashboard/convenzioni-assegnate', icon: Building2, label: 'Convenzioni Assegnate', badge: convenzioniAssegnateCount, badgeColor: 'emerald', badgeTestId: 'convenzioni-assegnate-count' }
    ] : []),
    { to: '/dashboard/parametri-calcolo', icon: Calculator, label: 'Parametri Calcolo' },
    { to: '/dashboard/impostazioni', icon: Settings, label: 'Impostazioni' },
  ] : [];
  
  // Menu per Top Agent - Il Mio Reparto + convenzioni referente
  const topAgentItems = (user?.role === 'top_agent' && !impersonatedUser) ? [
    { to: '/dashboard/mio-reparto', icon: Building2, label: 'Il Mio Reparto' },
    { to: '/dashboard/convenzioni', icon: Handshake, label: 'Le Mie Convenzioni', badge: convenzioniPendingCount, badgeColor: 'red', badgeTestId: 'pending-count-badge' },
    // 2.6.4 — Voce visibile solo se l'agente ha almeno 1 convenzione assegnata come referente
    ...(convenzioniAssegnateCount > 0 ? [
      { to: '/dashboard/convenzioni-assegnate', icon: Building2, label: 'Convenzioni Assegnate', badge: convenzioniAssegnateCount, badgeColor: 'emerald', badgeTestId: 'convenzioni-assegnate-count' }
    ] : []),
  ] : [];

  // 2.6.7 — Menu Agente semplice referente di convenzioni.
  // Un agente (role='agente') può essere designato referente e deve poter accedere
  // al workspace. Non ha accesso ad altri menu Amministrazione: si mostra la sola voce
  // 'Convenzioni Assegnate'.
  const agenteConvenzioniItems = (user?.role === 'agente' && !impersonatedUser && convenzioniAssegnateCount > 0) ? [
    { to: '/dashboard/convenzioni-assegnate', icon: Building2, label: 'Convenzioni Assegnate', badge: convenzioniAssegnateCount, badgeColor: 'emerald', badgeTestId: 'convenzioni-assegnate-count' }
  ] : [];

  // Callback per chiudere sidebar mobile
  const handleNavItemClick = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex overflow-hidden">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - SEMPRE FISSA su desktop */}
      <aside 
        className={cn(
          "fixed inset-y-0 left-0 z-50 border-r transform transition-all duration-300 ease-in-out flex-shrink-0 overscroll-contain",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
          sidebarCollapsed ? "w-16" : "w-64",
          // Cambio colore sidebar durante impersonificazione
          impersonatedUser 
            ? "bg-gradient-to-b from-orange-50 to-amber-50 border-orange-300" 
            : "bg-white border-slate-200"
        )}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-screen">
          {/* Logo - FISSO IN ALTO - White Label per reparto */}
          <div className={cn(
            "flex items-center border-b flex-shrink-0 h-16 min-h-[64px] max-h-[64px]",
            sidebarCollapsed ? "justify-center px-2" : "justify-between px-4",
            impersonatedUser ? "border-orange-200" : "border-slate-200"
          )}>
            {!sidebarCollapsed && (
              <div className="flex items-center gap-3 overflow-hidden">
                {/* Priorità: 1) Logo Convenzione (attiva workspace o cliente convenzionato) →
                    2) Logo Reparto white-label → 3) Logo Machenoleggio */}
                {convenzioneFull?.logo_url ? (
                  <img
                    src={convenzioneFull.logo_url.startsWith('http') ? convenzioneFull.logo_url : `${process.env.REACT_APP_BACKEND_URL}${convenzioneFull.logo_url}`}
                    alt={convenzioneFull.nome}
                    className="h-20 max-h-20 w-full max-w-[220px] object-contain"
                    onError={(e) => { e.target.onerror = null; e.target.src = '/assets/logos/logo-dark.png?v=2026'; }}
                    data-testid="topbar-convenzione-logo"
                  />
                ) : activeConvenzione ? (
                  <div className="flex flex-col leading-tight" data-testid="topbar-convenzione-text">
                    <span className="text-sm font-bold text-slate-800">MaCheNoleggio</span>
                    <span className="text-xs font-semibold text-emerald-700 truncate max-w-[160px]">× {activeConvenzione.nome}</span>
                  </div>
                ) : myReparto?.logo_url && !myReparto?.is_sede_centrale ? (
                  <img 
                    src={myReparto.logo_url.startsWith('http') ? myReparto.logo_url : `${process.env.REACT_APP_BACKEND_URL}${myReparto.logo_url}`}
                    alt={myReparto.nome || 'Logo Reparto'} 
                    className="h-14 max-h-14 w-auto max-w-[180px] object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/assets/logos/logo-dark.png?v=2026';
                    }}
                  />
                ) : (
                  <img 
                    src="/assets/logos/logo-dark.png?v=2026" 
                    alt="MAcheNoleggio" 
                    className="h-10 max-h-10 w-auto object-contain"
                  />
                )}
              </div>
            )}
            {sidebarCollapsed && (
              <>
                {convenzioneFull?.logo_url ? (
                  <img
                    src={convenzioneFull.logo_url.startsWith('http') ? convenzioneFull.logo_url : `${process.env.REACT_APP_BACKEND_URL}${convenzioneFull.logo_url}`}
                    alt={convenzioneFull.nome}
                    className="h-10 w-10 max-h-10 max-w-10 object-contain"
                    onError={(e) => { e.target.onerror = null; e.target.src = '/assets/logos/icon-ma.png'; }}
                  />
                ) : myReparto?.logo_url && !myReparto?.is_sede_centrale ? (
                  <img 
                    src={myReparto.logo_url.startsWith('http') ? myReparto.logo_url : `${process.env.REACT_APP_BACKEND_URL}${myReparto.logo_url}`}
                    alt={myReparto.nome || 'Logo'} 
                    className="h-10 w-10 max-h-10 max-w-10 object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/assets/logos/icon-ma.png';
                    }}
                  />
                ) : (
                  <img 
                    src="/assets/logos/icon-ma.png" 
                    alt="MAcheNoleggio" 
                    className="h-8 w-8 object-contain"
                  />
                )}
              </>
            )}
            <button 
              className="lg:hidden p-1 rounded-lg hover:bg-slate-100"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation - SCROLLABILE con blocco propagazione scroll */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto min-h-0 overscroll-contain">
            {navItems.map((item) => (
              <NavItem 
                key={item.to} 
                {...item} 
                collapsed={sidebarCollapsed}
                onItemClick={handleNavItemClick}
              />
            ))}
            
            {/* Vetrina Esterna - Visibile solo a Admin e Agente */}
            {!isCliente && (
              <>
                {!sidebarCollapsed && (
                  <div className="pt-4 pb-2 px-4">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Link Utili
                    </span>
                  </div>
                )}
                {sidebarCollapsed && <div className="border-t border-slate-200 my-2" />}
                <NavItem 
                  to="/dashboard/moduli"
                  icon={FolderOpen}
                  label="Moduli Utili"
                  collapsed={sidebarCollapsed}
                  onItemClick={handleNavItemClick}
                />
                {/* Vetrina Esterna - Solo per Sede Centrale o Super Admin */}
                {showVetrina && (
                  <InternalLinkItem 
                    to="/vetrina"
                    icon={Store}
                    label="Vetrina Esterna"
                    collapsed={sidebarCollapsed}
                    onItemClick={handleNavItemClick}
                  />
                )}
              </>
            )}
            
            {/* Menu Amministrazione - nascosti durante impersonificazione */}
            {showAdminMenus && (
              <>
                {!sidebarCollapsed && (
                  <div className="pt-4 pb-2 px-4">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Amministrazione
                    </span>
                  </div>
                )}
                {sidebarCollapsed && <div className="border-t border-slate-200 my-2" />}
                {adminItems.map((item) => (
                  <NavItem 
                    key={item.to} 
                    {...item} 
                    collapsed={sidebarCollapsed}
                    onItemClick={handleNavItemClick}
                  />
                ))}
                {/* Importa Excel - Solo Super Admin o Admin Reparto 0 */}
                {canAccessImportExcel && (
                  <NavItem 
                    to="/dashboard/upload"
                    icon={Upload}
                    label="Importa Excel"
                    collapsed={sidebarCollapsed}
                    onItemClick={handleNavItemClick}
                  />
                )}
                {/* Menu esclusivo Super Admin */}
                {isSuperAdmin && superAdminItems.map((item) => (
                  <NavItem 
                    key={item.to} 
                    {...item} 
                    collapsed={sidebarCollapsed}
                    onItemClick={handleNavItemClick}
                  />
                ))}
                {/* Menu per Top Agent */}
                {topAgentItems.map((item) => (
                  <NavItem 
                    key={item.to} 
                    {...item} 
                    collapsed={sidebarCollapsed}
                    onItemClick={handleNavItemClick}
                  />
                ))}
              </>
            )}
            {/* 2.6.7 — Menu per Agente semplice referente di convenzioni.
                 Reso FUORI dal blocco Amministrazione (che è invisibile ai non-admin). */}
            {agenteConvenzioniItems.length > 0 && (
              <>
                {!sidebarCollapsed && (
                  <div className="pt-4 pb-2 px-4">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Le Mie Convenzioni
                    </span>
                  </div>
                )}
                {sidebarCollapsed && <div className="border-t border-slate-200 my-2" />}
                {agenteConvenzioniItems.map((item) => (
                  <NavItem
                    key={item.to}
                    {...item}
                    collapsed={sidebarCollapsed}
                    onItemClick={handleNavItemClick}
                  />
                ))}
              </>
            )}
          </nav>

          {/* Bottom section - FISSO IN BASSO (profilo + impersonazione + collapse) */}
          <div className={cn(
            "flex-shrink-0 border-t",
            sidebarCollapsed ? "p-2" : "p-4",
            activeConvenzione ? "border-emerald-200 bg-emerald-50/60" :
            (impersonatedUser ? "border-orange-200 bg-orange-50/50" : "border-slate-200 bg-slate-50/50")
          )}>
            {/* Blocco Convenzione Attiva */}
            {!sidebarCollapsed && activeConvenzione && (
              <div className="mb-4 space-y-2 p-3 rounded-md border border-emerald-300 bg-emerald-50" data-testid="active-convenzione-block">
                <div className="flex items-center gap-2 text-emerald-800">
                  <Handshake className="w-4 h-4" />
                  <span className="text-[10px] font-semibold uppercase tracking-wide">Convenzione</span>
                </div>
                <p className="text-sm font-bold text-emerald-900 leading-tight">{activeConvenzione.nome}</p>
                <p className="text-[10px] text-emerald-700 font-mono">{activeConvenzione.codice_univoco}</p>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full h-8 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-100 gap-1"
                  onClick={() => {
                    // Prima naviga fuori dalla pagina convenzione (per evitare che
                    // ConvenzioneOverviewPage rimonti e riattivi automaticamente la convenzione),
                    // POI ripulisci lo stato. Il navigate smonta la pagina, così l'useEffect di
                    // auto-riattivazione non parte più. Nessun reload = niente lampeggio + niente
                    // "difficoltà di exit".
                    const nomeConv = activeConvenzione?.nome;
                    exitConvenzione();
                    navigate('/dashboard', { replace: true });
                    if (nomeConv) {
                      toast.success(`Uscito da ${nomeConv}`, {
                        description: 'Torna alla vista completa Sede Centrale.',
                        duration: 3000,
                      });
                    }
                  }}
                  data-testid="btn-exit-convenzione"
                >
                  ← Torna alla vista completa
                </Button>
              </div>
            )}
            {/* Dropdown Impersonate - SOLO SUPER ADMIN */}
            {!sidebarCollapsed && isSuperAdmin && capogruppoList.length > 0 && (
              <div className="mb-4 space-y-2">
                <label className="flex items-center gap-1 text-xs font-medium text-slate-500">
                  <Eye className="w-3 h-3" />
                  Visualizza come
                </label>
                <Select
                  value={impersonatedUser?.id || 'admin'}
                  onValueChange={handleImpersonateChange}
                >
                  <SelectTrigger 
                    className={cn(
                      "w-full text-xs h-9",
                      impersonatedUser && "border-amber-400 bg-amber-50"
                    )}
                    data-testid="impersonate-select"
                  >
                    <SelectValue placeholder="Admin" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    <SelectItem value="admin">
                      <span className="font-medium">SUPER ADMIN</span> - Vista completa
                    </SelectItem>
                    
                    {/* Capogruppo con corona */}
                    {agentsList.capogruppo?.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-[10px] font-semibold text-amber-600 bg-amber-50 border-y border-amber-100 mt-1">
                          👑 CAPIGRUPPO
                        </div>
                        {agentsList.capogruppo.map(cg => (
                          <SelectItem key={cg.id} value={cg.id}>
                            <span className="flex items-center gap-1">
                              <Crown className="w-3 h-3 text-amber-500" />
                              {cg.name} ({cg.subordinati_count})
                            </span>
                          </SelectItem>
                        ))}
                      </>
                    )}
                    
                    {/* Altri Agenti */}
                    {agentsList.altri_agenti?.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-[10px] font-semibold text-slate-500 bg-slate-50 border-y border-slate-100 mt-1">
                          ALTRI AGENTI
                        </div>
                        {agentsList.altri_agenti.map(agent => (
                          <SelectItem key={agent.id} value={agent.id}>
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3 text-slate-400" />
                              {agent.name}
                            </span>
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
                {impersonatedUser && (
                  <p className="text-[10px] text-amber-600 flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    Visualizzando come {impersonatedUser.name}
                  </p>
                )}
              </div>
            )}
            
            {!sidebarCollapsed && (
              <div className="flex items-center gap-3 mb-4">
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                  impersonatedUser ? "bg-amber-100" : "bg-primary/10"
                )}>
                  <span className={cn(
                    "text-sm font-semibold",
                    impersonatedUser ? "text-amber-700" : "text-primary"
                  )}>
                    {impersonatedUser ? impersonatedUser.name?.charAt(0).toUpperCase() : user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    {impersonatedUser ? impersonatedUser.name : user?.name}
                  </p>
                  <p className="text-xs text-slate-500 capitalize">
                    {impersonatedUser ? 'Capogruppo (simulato)' : user?.role}
                  </p>
                </div>
              </div>
            )}
            {sidebarCollapsed ? (
              <button
                onClick={handleLogout}
                className="w-full p-2 rounded-lg hover:bg-red-50 text-slate-600 hover:text-red-600 transition-colors flex justify-center"
                title="Esci"
              >
                <LogOut className="w-5 h-5" />
              </button>
            ) : (
              <Button 
                variant="outline" 
                className="w-full justify-start gap-2 text-slate-600 hover:text-red-600 hover:border-red-200"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Esci
              </Button>
            )}
            
            {/* Collapse toggle button - Desktop only - ora dentro la sezione fissa */}
            <div className="hidden lg:flex justify-center pt-3 mt-3 border-t border-slate-200">
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
                title={sidebarCollapsed ? "Espandi menu" : "Comprimi menu"}
              >
                {sidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content - con margine per compensare la sidebar fissa */}
      <div className={cn(
        "flex-1 flex flex-col min-h-screen min-w-0",
        sidebarCollapsed ? "lg:ml-16" : "lg:ml-64",
        impersonatedUser && "ring-4 ring-orange-400 ring-inset"
      )}>
        {/* Impersonation Warning Bar - Parametri Economici Agente */}
        {impersonatedUser && (
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-3 flex items-center justify-between shadow-lg z-40 sticky top-0">
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5 animate-pulse" />
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                <span className="font-bold text-sm tracking-wide">
                  ⚠️ STAI OPERANDO COME: {impersonatedUser.name}
                </span>
                <span className="text-xs sm:text-sm opacity-90">
                  Parametri economici e provvigioni basati sul suo profilo
                </span>
              </div>
            </div>
            <Button 
              variant="secondary"
              size="sm"
              onClick={stopImpersonate}
              className="bg-white text-orange-600 hover:bg-orange-100 font-semibold shadow-md"
              data-testid="stop-impersonate-btn"
            >
              ← Torna ad Admin
            </Button>
          </div>
        )}
        
        {/* Top bar */}
        <header className={cn(
          "sticky z-30 glass h-16 flex items-center px-4 lg:px-8 flex-shrink-0",
          impersonatedUser ? "top-[52px]" : "top-0"
        )}>
          <button 
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 mr-4"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          
          <div className="flex-1" />
          
          {/* Widget Supporto */}
          {supportoInfo && (supportoInfo.nome_referente || supportoInfo.email || supportoInfo.telefono) && (
            <div className="relative">
              <button
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-blue-600 transition-colors"
                onMouseEnter={() => setShowSupportoPopup(true)}
                onMouseLeave={() => setShowSupportoPopup(false)}
                onClick={() => setShowSupportoPopup(!showSupportoPopup)}
                data-testid="supporto-widget-btn"
                title="Supporto"
              >
                <LifeBuoy className="w-5 h-5" />
              </button>
              
              {/* Popup Supporto */}
              {showSupportoPopup && (
                <div 
                  className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-lg border p-4 z-50"
                  onMouseEnter={() => setShowSupportoPopup(true)}
                  onMouseLeave={() => setShowSupportoPopup(false)}
                >
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                    <LifeBuoy className="w-5 h-5 text-blue-500" />
                    <span className="font-semibold text-slate-800">Assistenza</span>
                    {supportoInfo.is_fallback && (
                      <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">Sede Centrale</span>
                    )}
                  </div>
                  
                  {supportoInfo.nome_referente && (
                    <div className="mb-3">
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Referente</p>
                      <p className="font-medium text-slate-800">{supportoInfo.nome_referente}</p>
                    </div>
                  )}
                  
                  {supportoInfo.email && (
                    <a 
                      href={`mailto:${supportoInfo.email}`}
                      className="flex items-center gap-2 p-2 rounded-lg hover:bg-blue-50 text-slate-700 hover:text-blue-600 transition-colors mb-2"
                      data-testid="supporto-email-link"
                    >
                      <Mail className="w-4 h-4" />
                      <span className="text-sm">{supportoInfo.email}</span>
                    </a>
                  )}
                  
                  {supportoInfo.telefono && (
                    <a 
                      href={`tel:${supportoInfo.telefono}`}
                      className="flex items-center gap-2 p-2 rounded-lg hover:bg-green-50 text-slate-700 hover:text-green-600 transition-colors"
                      data-testid="supporto-phone-link"
                    >
                      <Phone className="w-4 h-4" />
                      <span className="text-sm">{supportoInfo.telefono}</span>
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </header>

        {/* Fase 2 Task 2.4 — Bell notifiche in-app (solo non-clienti) */}
        <div className="absolute top-3 right-3 z-30 hidden lg:block">
          <NotificationBell />
        </div>

        {/* Page content - scrollabile indipendentemente */}
        <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
          <div className="max-w-full">
            <CatalogUpdateBanner />
            <Outlet />
            <AyvensQuestionnaireDialog />
          </div>
        </main>
      </div>
      
      {/* Broadcast Popup - mostra notifiche broadcast non lette */}
      <BroadcastPopup onUnreadCountChange={setBroadcastCount} />

      {/* Fase 2 Task 2.4 — Pulsante flottante supporto cliente (solo utenti cliente) */}
      {isCliente && <RichiediSupportoButton />}
    </div>
  );
};

export default DashboardLayout;
